/**
 * 前端错误监控 SDK - 核心追踪器
 * 唯一对外业务门面，业务代码只与 tracker 交互
 */
import type {
  ErrorTrackerConfig,
  FrontendErrorEvent,
  ErrorEventType,
  ErrorLevel,
  UserContext,
  RouteContext,
  ProjectContext,
  CaptureExceptionOptions,
  ApiErrorContext,
  BreadcrumbEntry,
} from './types';
import {
  getSessionId,
  setUserContext,
  getUserContext,
  setRouteContext,
  getRouteContext,
  setProjectContext,
  getProjectContext,
} from './context';
import { addBreadcrumb, getBreadcrumbs, configureBreadcrumbs } from './breadcrumbs';
import { sanitizeMessage, sanitizeStack, sanitizeUrl, sanitizeObject } from './sanitize';
import { enqueue, initTransport, flush } from './transport';
import { installGlobalHandlers } from './global-handlers';
import { shouldIgnoreRuntimeErrorMessage } from './ignored-errors';
import { generateUUID } from '@/lib/utils';

let trackerConfig: ErrorTrackerConfig | null = null;

/** 生成 UUID v4（兼容非安全上下文） */
function generateEventId(): string {
  return generateUUID();
}

/** 构造基础事件，补充公共字段 */
function buildBaseEvent(
  type: ErrorEventType,
  level: ErrorLevel,
  message: string,
  options?: CaptureExceptionOptions
): FrontendErrorEvent {
  const cfg = trackerConfig!;
  const routeCtx = getRouteContext();
  const userCtx = getUserContext();
  const projectCtx = getProjectContext();

  const userField: UserContext | undefined = userCtx
    ? {
        userId: userCtx.userId,
        teamId: projectCtx?.teamId ?? userCtx.teamId,
        projectId: projectCtx?.projectId ?? userCtx.projectId,
      }
    : undefined;

  return {
    eventId: generateEventId(),
    occurredAt: new Date().toISOString(),
    type,
    level,
    message: sanitizeMessage(message),
    release: cfg.release,
    environment: cfg.environment,
    url: sanitizeUrl(typeof window !== 'undefined' ? window.location.href : ''),
    pathname: routeCtx.pathname,
    routeName: routeCtx.routeName,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 300) : '',
    sessionId: getSessionId(),
    user: userField,
    tags: options?.tags,
    breadcrumbs: getBreadcrumbs(),
    extra: options?.extra ? (sanitizeObject(options.extra) as Record<string, unknown>) : undefined,
    requestId: options?.requestId,
    fingerprintHint: options?.fingerprintHint ?? null,
  };
}

/** 开发模式日志输出 */
function devLog(message: string, error?: unknown): void {
  const cfg = trackerConfig;
  if (!cfg) return;
  if (!cfg.enabled || cfg.debug) {
    console.warn(`[ErrorTracker DEV] captured: ${message}`, error ?? '');
  }
}

/** 提交事件（应用 beforeSend 并入队） */
function submitEvent(event: FrontendErrorEvent): void {
  if (!trackerConfig?.enabled) {
    devLog(event.message);
    return;
  }

  let finalEvent = event;
  if (trackerConfig.beforeSend) {
    const result = trackerConfig.beforeSend(event);
    if (result === null) return; // 被过滤
    finalEvent = result;
  }

  enqueue(finalEvent);
}

// ─── 公开 API ────────────────────────────────────────────────────

/** 初始化 SDK */
function init(cfg: ErrorTrackerConfig): void {
  trackerConfig = {
    maxBreadcrumbs: 20,
    flushInterval: 3000,
    flushBatchSize: 5,
    sampleRate: 1.0,
    ...cfg,
  };
  configureBreadcrumbs(trackerConfig.maxBreadcrumbs!);
  initTransport(trackerConfig);
  installGlobalHandlers(captureException);
}

/** 捕获异常对象 */
function captureException(error: unknown, options?: CaptureExceptionOptions): void {
  const err = error instanceof Error ? error : new Error(String(error));
  if (shouldIgnoreRuntimeErrorMessage(err.message)) return;

  const event = buildBaseEvent('runtime_error', options?.level ?? 'error', err.message, options);
  event.name = err.name;
  event.stack = sanitizeStack(err.stack);

  if (options?.extra?.componentStack) {
    event.extra = {
      ...(event.extra ?? {}),
      componentStack: String(options.extra.componentStack).slice(0, 3000),
    };
    event.type = 'react_boundary';
  } else if (options?.extra?.promiseRejectionReason !== undefined) {
    event.type = 'unhandled_rejection';
  }

  devLog(err.message, err);
  submitEvent(event);
}

/** 捕获纯文本消息 */
function captureMessage(message: string, options?: CaptureExceptionOptions): void {
  const event = buildBaseEvent('custom', options?.level ?? 'info', message, options);
  devLog(message);
  submitEvent(event);
}

/** 捕获 API 错误（Axios 拦截器使用） */
function captureApiError(error: unknown, context?: ApiErrorContext): void {
  const err = error instanceof Error ? error : new Error(String(error));
  const event = buildBaseEvent('api_error', 'error', err.message, {
    tags: context?.tags,
    requestId: context?.requestId,
  });
  event.name = err.name;
  event.stack = sanitizeStack(err.stack);
  event.request = context
    ? {
        method: context.method,
        url: sanitizeUrl(context.url),
        statusCode: context.statusCode,
      }
    : undefined;

  devLog(err.message, err);
  submitEvent(event);
}

/** 设置当前用户上下文 */
function setUser(ctx: UserContext | null): void {
  setUserContext(ctx);
}

/** 设置当前路由上下文 */
function setRoute(ctx: RouteContext): void {
  setRouteContext(ctx);
  addBreadcrumb({
    type: 'navigation',
    message: ctx.pathname,
    data: { routeName: ctx.routeName },
  });
}

/** 设置当前项目上下文 */
function setProject(ctx: ProjectContext | null): void {
  setProjectContext(ctx);
}

/** 添加 breadcrumb */
function breadcrumb(entry: Omit<BreadcrumbEntry, 'timestamp'> & { timestamp?: string }): void {
  addBreadcrumb(entry);
}

/** 判断 SDK 是否已启用 */
function isEnabled(): boolean {
  return trackerConfig?.enabled ?? false;
}

/** 手动 flush（页面卸载等场景） */
async function flushEvents(): Promise<void> {
  await flush();
}

export const errorTracker = {
  init,
  captureException,
  captureMessage,
  captureApiError,
  setUserContext: setUser,
  setRouteContext: setRoute,
  setProjectContext: setProject,
  addBreadcrumb: breadcrumb,
  flush: flushEvents,
  isEnabled,
};
